import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiUser, FiMapPin, FiCalendar, FiBookOpen, FiAward, FiBriefcase, FiMail, FiEye, FiUserPlus, FiCheck, FiClock, FiUserX, FiFlag } from 'react-icons/fi';
import ContentReportModal from './components/ContentReportModal';
import MutualConnections from './components/MutualConnections';

// Helper function to format degree type for public display (abbreviated form only)
const formatDegreeType = (degreeType) => {
  const degreeMap = {
    'bachelors': "Bachelor's Degree",
    'masters': "Master's Degree", 
    'phd': 'Ph.D.',
    'jd': 'J.D.',
    'mba': 'M.B.A.',
    'edd': 'Ed.D.',
    'md': 'M.D.',
    'deng': 'D.Eng.'
  };
  
  return degreeMap[degreeType] || degreeType;
};

// Helper function to format LinkedIn date objects
const formatDate = (dateObj) => {
  if (!dateObj) return '';
  
  // Handle LinkedIn date objects with year and month properties
  if (typeof dateObj === 'object' && dateObj.year) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    if (dateObj.month && dateObj.month >= 1 && dateObj.month <= 12) {
      return `${monthNames[dateObj.month - 1]} ${dateObj.year}`;
    }
    return dateObj.year.toString();
  }
  
  // Fallback for string dates (if any exist)
  if (typeof dateObj === 'string') {
    try {
      return new Date(dateObj).toLocaleDateString('en-US', { 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateObj;
    }
  }
  
  return '';
};

const AlumniPublicProfile = () => {
  const { alumniId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchProfile();
    checkConnectionStatus();
  }, [alumniId]);

  const checkConnectionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/connections/status/${alumniId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data);
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    } finally {
      setCheckingConnection(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/alumni/login';
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/directory/profile/${alumniId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/alumni/login';
          return;
        }
        if (response.status === 404) {
          setError('Alumni profile not found or not available for viewing.');
        } else {
          setError('Failed to load profile. Please try again.');
        }
        return;
      }

      const data = await response.json();
      setProfile(data);
    } catch (error) {
      console.error('Profile fetch error:', error);
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <FiUser className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Profile Not Available</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Profile Header */}
        <div 
          className="px-6 py-8 text-white relative"
          style={profile.background_image_url ? {
            backgroundImage: `url(${profile.background_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          } : {}}
        >
          {/* Background overlay for text readability */}
          <div className={`absolute inset-0 ${profile.background_image_url ? 'bg-black bg-opacity-50' : 'bg-gradient-to-r from-blue-600 to-blue-700'}`}></div>
          <div className="relative z-10 flex items-center space-x-6">
            <div className="flex-shrink-0">
              {profile.headshot_url ? (
                <img
                  className="h-24 w-24 rounded-full object-cover border-4 border-white"
                  src={profile.headshot_url}
                  alt={`${profile.first_name} ${profile.last_name}`}
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center border-4 border-white">
                  <FiUser className="h-12 w-12 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold">
                {profile.first_name} {profile.last_name}
              </h1>
              {profile.show_current_position && profile.current_position && (
                <p className="text-xl text-blue-100 mt-1">{profile.current_position}</p>
              )}
              {profile.show_current_employer && profile.current_employer && (
                <div className="flex items-center mt-1">
                  {profile.current_company_logo && (
                    <img
                      src={profile.current_company_logo}
                      alt={profile.current_employer}
                      className="h-6 w-6 rounded mr-2 bg-white"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <p className="text-lg text-blue-200">{profile.current_employer}</p>
                </div>
              )}
              <div className="flex items-center mt-3 text-blue-100">
                <FiEye className="h-4 w-4 mr-2" />
                <span className="text-sm">{profile.profile_views} profile views</span>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="relative z-10 mt-6 flex space-x-3">
            {!checkingConnection && connectionStatus && (
              <>
                {connectionStatus.status === 'none' && (
                  <button
                    onClick={() => setShowConnectionModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-white border-opacity-30 rounded-md shadow-sm text-sm font-medium text-white bg-white bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                  >
                    <FiUserPlus className="h-4 w-4 mr-2" />
                    Connect
                  </button>
                )}
                {connectionStatus.status === 'pending_sent' && (
                  <button
                    disabled
                    className="inline-flex items-center px-4 py-2 border border-white border-opacity-30 rounded-md shadow-sm text-sm font-medium text-white bg-white bg-opacity-10 cursor-not-allowed opacity-75"
                  >
                    <FiClock className="h-4 w-4 mr-2" />
                    Request Pending
                  </button>
                )}
                {connectionStatus.status === 'pending_received' && (
                  <button
                    onClick={() => setShowConnectionModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-white border-opacity-30 rounded-md shadow-sm text-sm font-medium text-white bg-white bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                  >
                    <FiUserPlus className="h-4 w-4 mr-2" />
                    Respond to Request
                  </button>
                )}
                {connectionStatus.status === 'connected' && (
                  <button
                    disabled
                    className="inline-flex items-center px-4 py-2 border border-green-400 border-opacity-50 rounded-md shadow-sm text-sm font-medium text-white bg-green-500 bg-opacity-20 cursor-not-allowed"
                  >
                    <FiCheck className="h-4 w-4 mr-2" />
                    Connected
                  </button>
                )}
                {connectionStatus.status === 'blocked' && (
                  <button
                    disabled
                    className="inline-flex items-center px-4 py-2 border border-red-400 border-opacity-50 rounded-md shadow-sm text-sm font-medium text-white bg-red-500 bg-opacity-20 cursor-not-allowed"
                  >
                    <FiUserX className="h-4 w-4 mr-2" />
                    Blocked
                  </button>
                )}
              </>
            )}
            {connectionStatus?.status !== 'self' && (
              <>
                <button
                  onClick={() => setShowMessageModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-white border-opacity-30 rounded-md shadow-sm text-sm font-medium text-white bg-white bg-opacity-10 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                >
                  <FiMail className="h-4 w-4 mr-2" />
                  Send Message
                </button>
              </>
            )}
          </div>
        </div>

        {/* Profile Content */}
        <div className="px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Section */}
              {profile.show_about && profile.about && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
                  <div className="prose max-w-none text-gray-700">
                    {profile.about.split('\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills Section */}
              {profile.show_skills && profile.skills && profile.skills.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Skills</h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Work Experience Section */}
              {profile.show_work_experience && profile.experience_history && profile.experience_history.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Work Experience</h2>
                  <div className="space-y-6">
                    {profile.experience_history.map((job, index) => (
                      <div key={index} className="border-l-4 border-blue-200 pl-6 pb-6">
                        <div className="flex items-start space-x-4">
                          {job.company_logo_url && (
                            <img
                              src={job.company_logo_url}
                              alt={job.company}
                              className="h-12 w-12 rounded object-contain bg-white border border-gray-200 flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                            <div className="flex items-center mt-1">
                              <span className="text-base font-medium text-blue-600">{job.company}</span>
                              {job.location && (
                                <>
                                  <span className="mx-2 text-gray-400">•</span>
                                  <span className="text-sm text-gray-600">{job.location}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center mt-1 text-sm text-gray-500">
                              <FiCalendar className="h-4 w-4 mr-1" />
                              <span>
                                {formatDate(job.start_date)}
                                {job.start_date && ' - '}
                                {job.is_current ? 'Present' : formatDate(job.end_date)}
                              </span>
                              {job.duration && (
                                <>
                                  <span className="mx-2">•</span>
                                  <span>{job.duration}</span>
                                </>
                              )}
                            </div>
                            {job.description && (
                              <div className="mt-3 text-gray-700 text-sm">
                                <p className="whitespace-pre-line">{job.description}</p>
                              </div>
                            )}
                            {job.skills && job.skills.length > 0 && (
                              <div className="mt-3">
                                <div className="flex flex-wrap gap-1">
                                  {job.skills.map((skill, skillIndex) => (
                                    <span
                                      key={skillIndex}
                                      className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                                    >
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience Summary Section */}
              {profile.show_years_experience && profile.years_experience && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Experience Summary</h2>
                  <div className="flex items-center text-gray-700">
                    <FiBriefcase className="h-5 w-5 mr-3 text-gray-500" />
                    <span>{profile.years_experience} years of professional experience</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Education */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
                <div className="space-y-3">
                  {profile.show_university && profile.university && (
                    <div className="flex items-start">
                      <FiMapPin className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                      <span className="text-gray-700">{profile.university}</span>
                    </div>
                  )}
                  
                  {profile.show_degree_type && profile.degree_type && (
                    <div className="flex items-start">
                      <FiAward className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                      <span className="text-gray-700">{formatDegreeType(profile.degree_type)}</span>
                    </div>
                  )}
                  
                  {profile.show_major && profile.major && (
                    <div className="flex items-start">
                      <FiBookOpen className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                      <span className="text-gray-700">{profile.major}</span>
                    </div>
                  )}
                  
                  {profile.show_graduation_year && profile.graduation_year && (
                    <div className="flex items-start">
                      <FiCalendar className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                      <span className="text-gray-700">Class of {profile.graduation_year}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              {profile.show_location && profile.location && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Location</h3>
                  <div className="flex items-start">
                    <FiMapPin className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                    <span className="text-gray-700">{profile.location}</span>
                  </div>
                </div>
              )}

              {/* Mutual Connections */}
              <MutualConnections 
                alumniId={parseInt(alumniId)} 
                mutualConnectionsCount={profile.mutual_connections_count || 0}
              />
            </div>
          </div>

        </div>

        {/* Report Button - Bottom Left */}
        <div className="px-6 pb-4">
          <button
            onClick={() => setShowReportModal(true)}
            className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-red-600 transition-colors"
            title="Report user"
          >
            <FiFlag className="h-3 w-3 mr-1" />
            Report
          </button>
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <MessageModal
          recipient={profile}
          onClose={() => setShowMessageModal(false)}
        />
      )}

      {/* Connection Modal */}
      {showConnectionModal && (
        <ConnectionModal
          recipient={profile}
          connectionStatus={connectionStatus}
          onClose={() => {
            setShowConnectionModal(false);
            checkConnectionStatus(); // Refresh connection status after action
          }}
        />
      )}

      {/* Report User Modal */}
      {showReportModal && profile && (
        <ContentReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType="profile"
          contentId={parseInt(alumniId)}
          reportedUserId={parseInt(alumniId)}
          contentPreview={`${profile.first_name} ${profile.last_name} - ${profile.current_position || 'Alumni'} at ${profile.current_employer || profile.university}`}
        />
      )}
    </div>
  );
};

const MessageModal = ({ recipient, onClose }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const sendMessage = async () => {
    if (!subject.trim() || !message.trim()) {
      setError('Please fill in both subject and message fields.');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/alumni/login';
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: recipient.id,
          subject: subject.trim(),
          message: message.trim()
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/alumni/login';
          return;
        }
        throw new Error('Failed to send message');
      }

      onClose();
    } catch (error) {
      console.error('Message send error:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Send Message to {recipient.first_name} {recipient.last_name}
          </h3>
        </div>
        
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Message subject..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Write your message..."
            />
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={sendMessage}
            disabled={sending}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConnectionModal = ({ recipient, connectionStatus, onClose }) => {
  const [connectionType, setConnectionType] = useState('professional');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const sendConnectionRequest = async () => {
    if (!recipient) return;

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/alumni/login';
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/connections/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: recipient.id,
          connection_type: connectionType,
          message: message.trim() || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send connection request');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Connection request error:', error);
      setError(error.message);
    } finally {
      setSending(false);
    }
  };

  const respondToRequest = async (action) => {
    if (!connectionStatus?.connection_id) return;

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/alumni/login';
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/connections/respond`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request_id: connectionStatus.connection_id,
          action: action
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to respond to connection request');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Connection response error:', error);
      setError(error.message);
    } finally {
      setSending(false);
    }
  };

  const isRespondingToRequest = connectionStatus?.status === 'pending_received';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-full sm:max-w-md mx-2 sm:mx-0">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isRespondingToRequest ? 'Connection Request' : 'Connect with Alumni'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {success ? (
            <div className="text-center py-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <FiCheck className="h-6 w-6 text-green-600" />
              </div>
              <p className="mt-3 text-sm text-gray-900">
                {isRespondingToRequest ? 'Response sent successfully!' : 'Connection request sent successfully!'}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className="flex items-center space-x-3 mb-4">
                  {recipient.headshot_url ? (
                    <img
                      className="h-12 w-12 rounded-full object-cover"
                      src={recipient.headshot_url}
                      alt={`${recipient.first_name} ${recipient.last_name}`}
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                      <FiUser className="h-6 w-6 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">
                      {recipient.first_name} {recipient.last_name}
                    </p>
                    {recipient.current_position && (
                      <p className="text-sm text-gray-600">{recipient.current_position}</p>
                    )}
                    {recipient.current_employer && (
                      <p className="text-sm text-gray-600">{recipient.current_employer}</p>
                    )}
                  </div>
                </div>
              </div>

              {isRespondingToRequest ? (
                <>
                  {connectionStatus.message && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Message:</span> {connectionStatus.message}
                      </p>
                    </div>
                  )}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => respondToRequest('accept')}
                      disabled={sending}
                      className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      {sending ? 'Processing...' : 'Accept'}
                    </button>
                    <button
                      onClick={() => respondToRequest('decline')}
                      disabled={sending}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                      {sending ? 'Processing...' : 'Decline'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Connection Type
                    </label>
                    <select
                      value={connectionType}
                      onChange={(e) => setConnectionType(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="professional">Professional</option>
                      <option value="classmate">Classmate</option>
                      <option value="colleague">Colleague</option>
                      <option value="friend">Friend</option>
                      <option value="mentor">Mentor</option>
                      <option value="mentee">Mentee</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Personal Message (Optional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={`Hi ${recipient.first_name}, I'd like to connect with you...`}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      rows={4}
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1">{message.length}/500 characters</p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={onClose}
                      disabled={sending}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendConnectionRequest}
                      disabled={sending}
                      className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {sending ? 'Sending...' : 'Send Request'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlumniPublicProfile;